import {
  ContentDetailQueryParams,
  ContentDetailSummary,
} from '@raydiant/api-client-js';
import { useTranslation } from 'react-i18next';
import { Popover } from '@headlessui/react';
import cn from 'classnames';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Center from 'raydiant-elements/layout/Center';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import { isAfter } from 'date-fns';

import * as paths from '../../../routes/paths';
import ChartCard from '../ChartCard/ChartCard';
import PlaceholderPresentationImage from './assets/placeholder-presentation.svg';
import PopoverButton from '../../../components/Popover/PopoverButton';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import {
  getDaysOfPeriod,
  getDisplayValue,
} from '../ShopperEngagementInsights/utils';
import { formatDateLabel } from '../utils';
import { presentationPageActions } from '../../PresentationPage';
import { useAffectedDevices, usePresentation } from '../../../hooks';
import CalendarIcon from './assets/calendar-icon.svg';
import CalendarPeriodIcon from './assets/calendar-period-icon.svg';
import SearchIcon from './assets/search-icon.svg';
import config from '../../../config';

const { supportAppUrl } = config;

const BG_COLOR = 'bg-[#FEF8DF]';

export default function PlaybackSummary({
  playbackSummaryData,
  isSidebarOpen,
  queryParams,
}: {
  playbackSummaryData: ContentDetailSummary;
  isSidebarOpen: boolean;
  queryParams: ContentDetailQueryParams;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: presentationInfo, isLoading: isLoadingPresentationInfo } =
    usePresentation(
      playbackSummaryData.presentationId || queryParams.presentation_id,
    );

  const showThumbnail = presentationInfo?.thumbnailUrl;
  const showIcon = presentationInfo?.iconUrl;
  const showDefaultImage = PlaceholderPresentationImage;

  let lastTimePlayed = playbackSummaryData.lastTimePlayed;
  if (isAfter(new Date(lastTimePlayed), new Date(queryParams.to))) {
    lastTimePlayed = queryParams.to;
  }

  const { data: statusData } = useAffectedDevices({
    presentationIds: playbackSummaryData.presentationId
      ? [playbackSummaryData.presentationId]
      : [],
    playlistIds: [],
    folderIds: [],
  });

  const navigateToContentLibrary = useCallback(
    (presentationId: string = '') => {
      dispatch(
        presentationPageActions.clearUnsavedPresentation(presentationId),
      );

      const url = paths.editPresentation(presentationId, {
        backTo: paths.library(),
        backToLabel: 'Back to Library',
        saveTo: paths.library(),
      });
      window.open(url, '_blank');
    },
    [dispatch],
  );
  return (
    <ChartCard
      title={
        <div className="flex items-center justify-between">
          <h2 className="text-black text-xl font-bold">
            {playbackSummaryData.presentationName || presentationInfo?.name}
          </h2>
          {statusData && statusData.length > 0 && (
            <div className="bg-[#5BD591] text-white text-base font-semibold py-1.5 px-5 rounded-3xl">
              {t('analytics.contentDetailPage.playbackSummary.status.active')}
            </div>
          )}
          {statusData && statusData.length === 0 && (
            <div className="bg-[#FA3D4A] text-white text-base font-semibold py-1.5 px-5 rounded-3xl">
              {t('analytics.contentDetailPage.playbackSummary.status.inactive')}
            </div>
          )}
        </div>
      }
      showTimeInterval={false}
    >
      <div className="relative flex flex-wrap xl:flex-nowrap">
        <div className="xl:mr-6">
          {isLoadingPresentationInfo ? (
            <Center className="w-[480px]">
              <CircularProgress size={30}></CircularProgress>
            </Center>
          ) : (
            <img
              className="w-[480px] max-h-[270px] bg-cover rounded-t-xl"
              src={showThumbnail || showIcon || showDefaultImage}
              alt="Presentation"
            />
          )}
          <button
            className="mb-6 w-full mt-3 py-4 text-lg btn sm:mini xl:mb-0"
            onClick={() =>
              navigateToContentLibrary(
                playbackSummaryData.presentationId || presentationInfo?.id,
              )
            }
          >
            {t('analytics.contentDetailPage.playbackSummary.button')}
          </button>
        </div>
        <div className="max-w-[624px] flex-grow md:max-w-full">
          <div className="flex flex-col justify-between sm:flex-row sm:items-center mb-6">
            <div className="flex">
              <h2 className="text-black text-xl font-semibold mr-2.5">
                {t('analytics.contentDetailPage.playbackSummary.title')}
              </h2>
              <Popover className="relative">
                <PopoverButton icon={<QuestionMarkIcon />} />
                <Popover.Panel className="absolute z-10 card popover">
                  <div className="overflow-hidden">
                    <p className="mb-3">
                      {t('analytics.contentDetailPage.playbackSummary.tooltip')}
                    </p>
                    <a
                      href={`${supportAppUrl}/visitor-insight/5imknKqrccCD9gmD6gKuJ1/how-do-i-use-the-insight-analytics-platform/6RjNYsyCvxraPhuSNUMYQo`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {t('analytics.learnmore')}
                    </a>
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
            <div className="flex mt-2 sm:ml-10 sm:mt-0 text-black text-xl font-semibold">
              <img src={CalendarPeriodIcon} alt="Calendar period icon" />
              <h2 className="ml-1">
                {formatDateLabel(queryParams.from, 'MMM D')} -{' '}
                {formatDateLabel(queryParams.to, 'MMM D, Y')}
              </h2>
            </div>
          </div>
          <div
            className={cn(
              'w-full grid sm:grid-rows-5 sm:grid-flow-col gap-x-6 gap-y-3 text-lg font-medium mr-9 pb-5 md:pb-0',
              'auto-rows-[minmax(40px,_auto)]',
            )}
          >
            <div className="flex items-center">
              <img src={SearchIcon} alt="Search Icon" />
              <h2 className="text-black ml-2">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.titleForPlace',
                )}
              </h2>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.totalPlays',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {getDisplayValue(playbackSummaryData.nOfPlays || 0)}
              </div>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.locations',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {getDisplayValue(playbackSummaryData.nOfLocations || 0)}
              </div>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.screens',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {getDisplayValue(playbackSummaryData.nOfScreens || 0)}
              </div>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.playlists',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {getDisplayValue(playbackSummaryData.nOfPlaylists || 0)}
              </div>
            </div>

            <div className="flex items-center">
              <img src={CalendarIcon} alt="Calendar Icon" />
              <h2 className="text-black ml-2">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.titleForTime',
                )}
              </h2>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.totalSelectedDays',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {getDaysOfPeriod(queryParams.from, queryParams.to)}
              </div>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.totalActiveDays',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {playbackSummaryData.nOfDays || 0}
              </div>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.firstPlayed',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {playbackSummaryData.firstTimePlayed
                  ? formatDateLabel(
                      playbackSummaryData.firstTimePlayed,
                      'MMM D, Y',
                    )
                  : 'N/A'}
              </div>
            </div>
            <div
              className={`flex items-center justify-between ${BG_COLOR} rounded-lg px-3`}
            >
              <div className="text-base font-medium">
                {t(
                  'analytics.contentDetailPage.playbackSummary.metricsData.lastPlayed',
                )}
              </div>
              <div className="text-black text-end font-semibold pl-8">
                {playbackSummaryData.lastTimePlayed
                  ? formatDateLabel(lastTimePlayed, 'MMM D, Y')
                  : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ChartCard>
  );
}
